import React, { useRef, useMemo, useState, useCallback, memo } from 'react';
import moment from 'moment';
import { useIntl } from 'umi';
import ChartsIndex from '@/pages/intelligence/components/ChartsComponents/ChartsIndex';
import ArrowdownText from '@/pages/intelligence/components/ArrowdownText';
import ChartBubble from '@/pages/intelligence/components/ChartBubble';
// import { TOP_CHARTS_TYPES } from '@/pages/intelligence/components/ChartBubble';
import useBarCharts from '@/pages/intelligence/components/Chart/hooks/useBarCharts';
import useSinglePieCharts from '@/pages/intelligence/components/Chart/hooks/useSinglePieCharts';
import useLineCharts from '@/pages/intelligence/components/Chart/hooks/useLineCharts';
import { moveZero } from '@/pages/intelligence/components/Chart/_config/topchartsChartUtils';
// import { newMathFormat } from '@/pages/intelligence/utils/commonUtils';
import { generateBaseBarSerie, getRealValueOfSerie } from '@/pages/intelligence/utils/echartsUtils';
import { generateBarOrPieData, generateLineData } from './utils';
import { SortMode } from '@/pages/intelligence/pages/TopCharts/_config/enums';
// import { ECHARTSCOLOR } from '@/pages/intelligence/utils/echartsConst';
import { COLUMNS_SORT_METADATAS_MAPPING } from '../../constants';
import type { IBaseProps } from '@/pages/intelligence/types/common';
import type { IBarSeriesOption } from '@/pages/intelligence/types/echart';
import type { IBarOrPieChartSourceData } from '@/pages/intelligence/components/Chart/types';
import type {
  ITopChartsCompaniesBaseProps,
  ITopChartsCompaniesRank,
  ITopChartsCompaniesSourceMetric,
} from '../../types';
import type { IChartsIndexInstance } from '@intelligence/components/ChartsComponents/ChartsIndex';

interface ICompaniesVisualisProps extends IBaseProps, ITopChartsCompaniesBaseProps { }

const getMetrics = (data: ITopChartsCompaniesSourceMetric[]): any[] => {
  if (!Array.isArray(data)) return [];
  return data.map((item) => {
    const { data_source: dataSource, metric } = item ?? {};
    return {
      ...item,
      metric: dataSource ?? '',
      order_source: dataSource ?? '',
      order_metric: metric ?? '',
    };
  });
};

const getBarOrPieDataOfComponent = (companiesRanks: ITopChartsCompaniesRank[]) => {
  if (!Array.isArray(companiesRanks)) return [];

  return companiesRanks.slice(0, 10).map((item) => {
    const { app_id: appId, name, image_url: imageUrl, source_metric_list: metrics } = item ?? {};
    return {
      ...item,
      id: appId ?? '',
      name: name ?? '',
      imageUrl: imageUrl ?? '',
      metric_value_list: getMetrics(metrics),
    };
  });
};

// 每个榜单，组装好各类可视化需要的数据，传递给可视化统一组件
const Visualis: React.FC<ICompaniesVisualisProps> = (props) => {
  const {
    isNodata,
    topChartsType = '',
    chartType = 'bar',
    fetchParams,
    barOrPieData,
    lineData,
    orderTypeInfo,
    titleNow = '', // 不能用这个字段做业务判断，只能做文案展示
    imageType,
    isNew = true,
    hoverCloneType,
  } = props;

  // console.log('Visualissfwefwef', props);

  const { formatMessage } = useIntl();
  const cardRef = useRef<HTMLDivElement>(null);
  const chartsInstance = useRef<IChartsIndexInstance>(null);
  const [chartLoading, setChartLoading] = useState(true);

  // 根据排序获得Sorted by 标题
  const orderNameInfo = useMemo<string>(() => {
    if (!fetchParams) return '';
    const { order_metric: orderMetric, ratio_type: ratioType } = fetchParams;
    const orderMetricInfo = Object.values(COLUMNS_SORT_METADATAS_MAPPING).find((item) => item?.key === orderMetric);
    const result = orderMetricInfo?.title ? formatMessage({ id: orderMetricInfo?.title }) : '';
    return ratioType === SortMode.Circle ? `${result}(%)` : result;
  }, [fetchParams?.order_metric, fetchParams?.ratio_type]);

  const { barNameList, barSeries, barOrPieSourceData } = useMemo(() => {
    const barOrPieDataOfComponent = getBarOrPieDataOfComponent(barOrPieData);
    const sourceData = generateBarOrPieData(topChartsType, barOrPieDataOfComponent);
    const result: {
      barOrPieSourceData: IBarOrPieChartSourceData[];
      barNameList: string[];
      barSeries: IBarSeriesOption[];
    } = {
      barOrPieSourceData: sourceData,
      barNameList: [],
      barSeries: [],
    };
    if (!Array.isArray(sourceData)) return result;
    result.barNameList = sourceData.map((item) => item?.name ?? '');
    // @ts-ignore
    result.barSeries = sourceData.map((item, index) => generateBaseBarSerie({ ...item, index }));
    return result;
  }, [topChartsType, barOrPieData]);

  const barFormatterTooltip = useCallback(
    (params: any[]) => {
      const paramsTemp = moveZero(params);
      return ChartBubble({
        topChartsType: hoverCloneType || topChartsType,
        chartType: 'bar',
        imageType: imageType ?? 'mobile',
        headerLeftTitle: '',
        headerRightTitle: titleNow,
        data: paramsTemp,
        startDate: fetchParams?.start_date,
        endDate: fetchParams?.end_date,
      });
    },
    [hoverCloneType, topChartsType, titleNow],
  );
  // 获取柱状图hooks
  const { barOption } = useBarCharts({
    topChartsType,
    imageType,
    setChartLoading,
    barNameList,
    seriesData: barSeries,
    barOrPieData: barOrPieSourceData,
    cardWidth: cardRef?.current?.clientWidth,
    tooltipFormatter: barFormatterTooltip,
  });

  const { pieOption } = useSinglePieCharts({
    chartType,
    chartsInstance,
    topChartsType,
    pieTitle: titleNow,
    pieData: barOrPieSourceData,
    titleNow,
    startDate: fetchParams?.start_date,
    endDate: fetchParams?.end_date,
  });

  const lineFormatterTooltip = (params: any[]) => {
    const paramsTemp = moveZero(params);
    const date = paramsTemp?.[0]?.axisValue;
    const dateStr = moment(date).format('YYYY-MM-DD');
    const sortedParamsTmp = [...paramsTemp].sort((a: any, b: any): number => {
      let aValue = getRealValueOfSerie(a?.value) || (typeof a?.value === 'function' ? a?.value(a?.data?.value) : Number.NaN);
      let bValue = getRealValueOfSerie(b?.value) || (typeof b?.value === 'function' ? b?.value(b?.data?.value) : Number.NaN);
      aValue = Number.parseFloat(aValue);
      bValue = Number.parseFloat(bValue);
      if (Number.isNaN(aValue) && Number.isNaN(bValue)) return 0;
      if (Number.isNaN(aValue)) return 1;
      if (Number.isNaN(bValue)) return -1;
      return bValue - aValue;
    });
    return ChartBubble({
      topChartsType: hoverCloneType || topChartsType,
      chartType: 'line',
      imageType: imageType ?? 'mobile',
      headerLeftTitle: dateStr,
      headerRightTitle: titleNow,
      data: sortedParamsTmp,
    });
  };
  // 获取折线图hooks
  const { lineOption } = useLineCharts({
    topChartsType,
    imageType,
    metric: 'source',
    dateType: fetchParams?.granularity,
    lineData: { source: generateLineData(topChartsType, lineData) },
    tooltipFormatter: lineFormatterTooltip,
  });

  return (
    <>
      {/* <ArrowdownText isNew={isNew} sortedData={orderNameInfo} order={orderTypeInfo} /> */}
      <div className="card-line" ref={cardRef}>
        <ChartsIndex
          ref={chartsInstance}
          isNodata={isNodata}
          chartLoading={chartLoading}
          chartType={chartType}
          barOption={barOption}
          lineOption={lineOption}
          pieOption={pieOption}
        />
      </div>
    </>
  );
};

export default memo(Visualis);
